import { fetchJsonObject } from '../chunk-U6MRWH6C.js';
import '../chunk-KLRGM47X.js';
import '../chunk-XR6TNFQL.js';
import { unwrapData } from '../chunk-HIWBYL4O.js';
import '../chunk-7CXQEHBJ.js';
import '../chunk-X34AGPO3.js';
import '../chunk-L6V3D5WY.js';
import '../chunk-7B3IWQPL.js';
import '../chunk-5WIUKZF6.js';
import '../chunk-HCZGW64N.js';
import '../chunk-FAYE5SWX.js';
import '../chunk-IAXDOK4V.js';
import '../chunk-BFUJOKOR.js';
import '../chunk-DQNP4KXT.js';
import { randomUuid } from '../chunk-WVLHT3DL.js';
import '../chunk-EE4CQDCZ.js';
import '../chunk-FCXSLTFD.js';
import '../chunk-BJ3WKLB6.js';
import '../chunk-INX63VAT.js';
import '../chunk-QFMXL6J4.js';
import '../chunk-6B5KO6TH.js';
import '../chunk-3QN2VZ56.js';
import '../chunk-ZE7H4TOZ.js';
import '../chunk-4A46FZY2.js';
import '../chunk-O2TUCS6S.js';
import '../chunk-G7JKLWJD.js';
import '../chunk-CMRKVSXA.js';
import '../chunk-GPJ572YS.js';
import { globalSingleton } from '../chunk-N7SZ66QO.js';
import '../chunk-LD6PO66H.js';
import '../chunk-AHISJLEK.js';
import '../chunk-QXYP3KHC.js';
import '../chunk-O5TCTRWM.js';
import '../chunk-O3OIBNP7.js';
import '../chunk-PZ5AY32C.js';

// src/quality/findings.ts
var record = (v) => v && typeof v === "object" && !Array.isArray(v) ? v : {};
var rows = (v) => Array.isArray(v) ? v.map(record) : [];
var count = (v) => typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 0;
var QUALITY_DIMENSION_LABELS = {
  structure: "Structure and coverage",
  envelopes: "Parameter envelopes",
  schema: "Schema",
  outputs: "Returns and reverts",
  evidence: "Confidence and evidence",
  provenance: "Provenance",
  protocol: "Protocol attribution"
};
var QUALITY_REASON_LABELS = {
  trace_coverage_unverified: "Execution trace coverage is unverified",
  flat_provider_calls_without_execution_topology: "The provider supplied a flat list of calls without execution topology. It cannot establish that every internal call was observed.",
  parameter_envelope_missing: "Parameter verification envelope is missing",
  parameter_schema_missing: "Parameter schema is unavailable",
  output_unavailable: "Recorded return or revert bytes are unavailable",
  code_identity_missing: "Runtime code identity at this transaction block is unavailable"
};
function qualityDetails(assessment) {
  const review = assessment.summary?.review ?? assessment.review;
  const artifacts = rows(assessment.artifacts).filter((a) => !["decoded", "empty", "no_function"].includes(String(a.outcome)) || typeof a.evidence === "string" && !["typed", "declared_schema", "empty"].includes(a.evidence));
  const checks = record(assessment.checks);
  const groups = /* @__PURE__ */ new Map();
  const group = (code, status) => {
    const key = `${status}:${code}`;
    if (!groups.has(key)) groups.set(key, { code, status, total: 0, unlocated: 0, findings: [] });
    return groups.get(key);
  };
  for (const finding of rows(checks.findings)) {
    if (finding.status !== "fail" && finding.status !== "unknown") continue;
    group(String(finding.code ?? "unspecified_check"), finding.status).findings.push(finding);
  }
  for (const [key, status] of [["by_code", "fail"], ["unavailable_by_code", "unknown"]]) {
    for (const [code, total] of Object.entries(record(checks[key]))) {
      if (count(total)) group(code, status).total = count(total);
    }
  }
  for (const g of groups.values()) {
    g.total = Math.max(g.total, g.findings.length);
    g.unlocated = g.total - g.findings.filter((f) => typeof f.path === "string" && f.path.length > 0).length;
  }
  const checkGroups = [...groups.values()].sort((a, b) => Number(a.status === "unknown") - Number(b.status === "unknown") || a.code.localeCompare(b.code));
  const dimensions = Object.values(record(checks.dimensions)).map(record);
  const unavailable = Math.max(count(review?.unavailable_checks), dimensions.reduce((n, d) => n + count(d.unavailable), 0));
  const failures = Math.max(count(review?.check_failures), dimensions.reduce((n, d) => n + count(d.failures), 0));
  const missing = assessment.observability?.missing_required_inputs ?? review?.missing_required_inputs;
  return {
    artifacts,
    checks: checkGroups,
    missingInputs: Array.isArray(missing) ? missing.filter((v) => typeof v === "string") : [],
    unlocatedArtifacts: Math.max(0, count(review?.artifact_gaps) + count(review?.coverage_gaps) - artifacts.length),
    unlocatedFailures: Math.max(0, failures - checkGroups.filter((g) => g.status === "fail").reduce((n, g) => n + g.total, 0)),
    unlocatedUnavailable: Math.max(0, unavailable - checkGroups.filter((g) => g.status === "unknown").reduce((n, g) => n + g.total, 0)),
    truncated: checks.findings_truncated === true
  };
}

// src/quality/index.ts
var qualityTerminal = (status) => ["succeeded", "completed_with_errors", "cancelled", "failed"].includes(status);
function qualityPublishedCopy(run, member) {
  if (run.request.mode !== "redecode_publish" || member.status !== "succeeded" || !["succeeded", "completed_with_errors"].includes(run.status) || !member.canonical_tx_id || !run.request.subject) return void 0;
  return { canonicalTxId: member.canonical_tx_id, scope: run.request.subject.user_id ? "user" : "tenant" };
}
var record2 = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
var text = (value) => typeof value === "string" && value ? value : void 0;
function assessmentFromBody(body) {
  const root = record2(body);
  for (const extras of [root?.extras, record2(root?.canonical_transaction)?.extras, record2(root?.canonical)?.extras]) {
    const saved = record2(record2(extras)?.tx_quality);
    if (saved) return saved;
  }
  return void 0;
}
var QUALITY_STATES = ["gaps", "not_assessable", "names_missing", "unverified", "not_assessed", "complete"];
var QUALITY_STATE_LABEL = {
  complete: "Complete",
  gaps: "Gaps",
  not_assessable: "Not assessable",
  not_assessed: "Not assessed",
  names_missing: "Names missing",
  unverified: "Unverified"
};
function qualityReviewOf(summary) {
  return summary?.summary?.review ?? summary?.review;
}
function qualityExecutionCoverage(summary) {
  return qualityReviewOf(summary)?.execution_coverage ?? summary?.axes?.execution_coverage ?? text(record2(summary?.checks?.axes)?.execution_coverage);
}
function qualityExecutionLabel(summary) {
  switch (qualityExecutionCoverage(summary)) {
    case "complete":
      return "Complete execution evidence";
    case "partial":
      return "Partial execution evidence";
    case "unverified":
      return "Execution coverage unverified";
    case "not_applicable":
      return "Not applicable";
    default:
      return "Execution coverage not assessed";
  }
}
function qualityStateOf(summary) {
  if (!summary || !summary.policy_result && !summary.completeness) return "not_assessed";
  const review = qualityReviewOf(summary);
  if (summary.completeness === "partial" || summary.policy_result === "fail" || (review?.check_failures ?? 0) > 0 || review?.status === "gaps" && !review.names_only) return "gaps";
  const execution = qualityExecutionCoverage(summary);
  if (summary.completeness === "complete_for_observed_scope" && (execution === "partial" || execution === "unverified")) return "unverified";
  if (summary.policy_result !== "pass" || summary.completeness !== "complete_for_observed_scope") return "not_assessable";
  if (review?.names_only || (review?.parameters_unnamed ?? 0) > 0) return "names_missing";
  if (review?.status === "unverified" || (review?.unavailable_checks ?? 0) > 0 || review?.unverified_dimensions?.length) return "unverified";
  return "complete";
}
function qualityCompletenessLabel(summary) {
  if (summary?.completeness !== "complete_for_observed_scope") return (summary?.completeness ?? "unknown").replaceAll("_", " ");
  if (qualityExecutionCoverage(summary) && !qualityReviewOf(summary)?.names_only && !(qualityReviewOf(summary)?.parameters_unnamed ?? 0) && qualityStateOf(summary) !== "gaps") return "Supplied artifacts decoded";
  switch (qualityStateOf(summary)) {
    case "names_missing":
      return "Decoded with missing names";
    case "unverified":
      return "Decoded; verification incomplete";
    case "gaps":
      return "Review gaps remain";
    case "not_assessable":
      return "Not assessable";
    default:
      return "Complete for observed scope";
  }
}
function qualityStateDetail(summary) {
  if (!summary || qualityStateOf(summary) === "not_assessed") return "No assessment saved with this row";
  const words = (value) => (value ?? "unknown").replaceAll("_", " ");
  const gaps = Number(record2(summary.counts)?.gaps);
  const review = qualityReviewOf(summary);
  return [
    `Policy ${words(summary.policy_result)}`,
    qualityCompletenessLabel(summary).toLowerCase(),
    qualityExecutionCoverage(summary) ? qualityExecutionLabel(summary) : void 0,
    Number.isFinite(gaps) && gaps > 0 ? `${gaps} gap${gaps === 1 ? "" : "s"}` : void 0,
    (review?.unavailable_checks ?? 0) > 0 ? `${review.unavailable_checks} unavailable checks` : void 0,
    summary.evaluator_version ? `evaluator ${summary.evaluator_version}` : void 0,
    summary.caller_check ? "from a check you ran, not saved with the row" : void 0
  ].filter(Boolean).join(" \xB7 ");
}
var CALLER_QUALITY_BATCH = 50;
var HIT_TTL_MS = 6e4;
var MISS_TTL_MS = 15e3;
var BATCH_WINDOW_MS = 25;
var FAILURE_BACKOFF_MS = 1e4;
function createCallerQualityLoader(client) {
  const cache = /* @__PURE__ */ new Map();
  let pending = /* @__PURE__ */ new Map();
  const flying = /* @__PURE__ */ new Map();
  let timer;
  let revision = 0;
  let quietUntil = 0;
  const listeners = /* @__PURE__ */ new Set();
  const flush = async () => {
    timer = void 0;
    const batch = [...pending.entries()];
    pending = /* @__PURE__ */ new Map();
    const asked = revision;
    for (const [key, p] of batch) flying.set(key, p.answer);
    for (let start = 0; start < batch.length; start += CALLER_QUALITY_BATCH) {
      const group = batch.slice(start, start + CALLER_QUALITY_BATCH);
      const byScope = /* @__PURE__ */ new Map();
      for (const entry of group) byScope.set(entry[1].scope ?? "", [...byScope.get(entry[1].scope ?? "") ?? [], entry]);
      for (const [scope, entries] of byScope) {
        let found;
        try {
          found = await qualitySource(client).summaries([...new Set(entries.map(([, p]) => p.id))], scope || void 0);
        } catch {
          found = void 0;
          quietUntil = Date.now() + FAILURE_BACKOFF_MS;
        }
        for (const [key, p] of entries) {
          const hit = found?.[p.id];
          const value = hit?.summary ? { ...hit.summary, caller_check: true } : void 0;
          if (found && asked === revision) cache.set(key, { at: Date.now(), value });
          if (flying.get(key) === p.answer) flying.delete(key);
          p.resolve(value);
        }
      }
    }
  };
  return {
    load(row, scope) {
      const source = record2(row);
      const id = text(source?.canonical_tx_id) ?? text(source?.id);
      if (!id || summaryFromRow(row)) return Promise.resolve(void 0);
      const decode = text(source?.body_etag) ?? text(source?.s3_etag) ?? text(source?.body_key) ?? text(source?.s3_key);
      const key = `${scope ?? ""}|${id}|${decode ?? ""}`;
      const cached = cache.get(key);
      if (cached && Date.now() - cached.at < (cached.value ? HIT_TTL_MS : MISS_TTL_MS)) return Promise.resolve(cached.value);
      const waiting = pending.get(key)?.answer ?? flying.get(key);
      if (waiting) return waiting;
      if (Date.now() < quietUntil) return Promise.resolve(void 0);
      let resolve;
      const answer = new Promise((done) => {
        resolve = done;
      });
      pending.set(key, { id, scope, answer, resolve });
      timer ??= setTimeout(() => {
        void flush();
      }, BATCH_WINDOW_MS);
      return answer;
    },
    clear() {
      cache.clear();
      flying.clear();
      quietUntil = 0;
      revision += 1;
      listeners.forEach((listener) => listener());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getRevision: () => revision
  };
}
var LOADERS = globalSingleton("quality/callerLoaders", () => /* @__PURE__ */ new WeakMap());
function callerQualityLoader(client) {
  let loader = LOADERS.get(client);
  if (!loader) {
    loader = createCallerQualityLoader(client);
    LOADERS.set(client, loader);
  }
  return loader;
}
function qualityRollup(summaries) {
  const counts = { complete: 0, gaps: 0, not_assessable: 0, not_assessed: 0, names_missing: 0, unverified: 0 };
  for (const summary of summaries) counts[qualityStateOf(summary)] += 1;
  return { total: summaries.length, counts, weakest: QUALITY_STATES.find((state) => counts[state] > 0) ?? null };
}
function summaryFromRow(row) {
  const source = record2(row);
  const saved = record2(source?.quality_summary);
  if (saved && Object.keys(saved).length) return saved;
  const policy_result = text(source?.quality_status), completeness = text(source?.quality_completeness);
  if (!policy_result && !completeness) return void 0;
  return { policy_result, completeness, evaluator_version: text(source?.quality_evaluator_version), policy_hash: text(source?.quality_policy_hash) };
}
var base = () => "/quality-runs";
function qualitySource(client) {
  return {
    async submit(request, idempotencyKey = randomUuid()) {
      return unwrapData(await client.post(base(), { ...request, idempotency_key: idempotencyKey })).run;
    },
    async list(cursor) {
      return unwrapData(await client.get(base(), { query: cursor ? { cursor } : {}, cacheTtlMs: 0 }));
    },
    async get(id) {
      return unwrapData(await client.get(`${base()}/${encodeURIComponent(id)}`, { cacheTtlMs: 0 })).run;
    },
    async results(id, cursor) {
      return unwrapData(await client.get(`${base()}/${encodeURIComponent(id)}/results`, { query: cursor ? { cursor } : {}, cacheTtlMs: 0 }));
    },
    /**
     * One member's result. `include: ['candidate']` also returns the re-decoded
     * envelope of a re-decode run — what the decode now SAYS, which the
     * assessment (what was read) cannot show, e.g. parameters that gained names.
     */
    async result(id, index, include) {
      const result = unwrapData(await client.get(
        `${base()}/${encodeURIComponent(id)}/results/${index}`,
        { cacheTtlMs: 0, query: { delivery: "url", ...include?.length ? { include: include.join(",") } : {} } }
      ));
      const { artifacts, ...hydrated } = result;
      for (const field of ["assessment", "candidate"]) {
        const artifact = artifacts?.[field];
        if (artifact) {
          const value = await fetchJsonObject(artifact.url, { credentials: "omit", referrerPolicy: "no-referrer" }, artifact.sha256);
          if (field === "assessment") hydrated.assessment = value;
          else hydrated.candidate = value;
        }
      }
      return hydrated;
    },
    async action(id, action) {
      return unwrapData(await client.post(`${base()}/${encodeURIComponent(id)}/${action}`, {})).run;
    },
    async transaction(id, scope) {
      return unwrapData(await client.get(`/transactions/${encodeURIComponent(id)}/quality`, { query: scope ? { scope } : {}, cacheTtlMs: 0 }));
    },
    /**
     * The caller's own saved summaries for a page of rows, in one request (the
     * batch form of the route above): only checks of the decode each row holds now.
     */
    async summaries(transactionIds, scope) {
      const ids = transactionIds.join(",");
      return unwrapData(await client.get(
        "/transactions/batch/quality",
        { query: scope ? { ids, scope } : { ids }, cacheTtlMs: 0 }
      )).summaries ?? {};
    },
    async save(name, request, runId) {
      return unwrapData(await client.post("/saved-views", { name, table_id: "transaction-quality", scope: "user", view_state: { quality: { schema_version: 1, request, last_run_id: runId } } }));
    },
    async views() {
      return unwrapData(await client.get("/saved-views", { query: { table_id: "transaction-quality" }, cacheTtlMs: 0 }));
    }
  };
}

export { CALLER_QUALITY_BATCH, QUALITY_DIMENSION_LABELS, QUALITY_REASON_LABELS, QUALITY_STATES, QUALITY_STATE_LABEL, assessmentFromBody, callerQualityLoader, createCallerQualityLoader, qualityCompletenessLabel, qualityDetails, qualityExecutionCoverage, qualityExecutionLabel, qualityPublishedCopy, qualityReviewOf, qualityRollup, qualitySource, qualityStateDetail, qualityStateOf, qualityTerminal, summaryFromRow };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map